import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@material-ui/core";
import PeopleIcon from "@material-ui/icons/People";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import ListIcon from "@material-ui/icons/List";
import SendIcon from "@material-ui/icons/Send";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  card: {
    cursor: "pointer",
    transition: "box-shadow 0.3s",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  select: {
    minWidth: 280,
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const GroupManagement = () => {
  const classes = useStyles();
  const history = useHistory();
  const [connections, setConnections] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data } = await api.get("/group-management/connections");
        setConnections(data);
        if (data.length > 0 && !selectedConnectionId) {
          setSelectedConnectionId(String(data[0].id));
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchConnections();
  }, [selectedConnectionId]);

  const handleNavigate = (path) => {
    if (!selectedConnectionId && path !== "/group-management") {
      return;
    }
    const url = selectedConnectionId
      ? `${path}?connectionId=${selectedConnectionId}`
      : path;
    history.push(url);
  };

  const cards = [
    {
      title: i18n.t("groupManagement.listGroups"),
      description: i18n.t("groupManagement.listGroupsDescription"),
      icon: <ListIcon style={{ fontSize: 48 }} />,
      path: "/group-management/groups",
    },
    {
      title: i18n.t("groupManagement.createGroup"),
      description: i18n.t("groupManagement.createGroupDescription"),
      icon: <GroupAddIcon style={{ fontSize: 48 }} />,
      path: "/group-management/groups/new",
    },
    {
      title: i18n.t("groupManagement.broadcast"),
      description: i18n.t("groupManagement.broadcastDescription"),
      icon: <SendIcon style={{ fontSize: 48 }} />,
      path: "/group-management/broadcast",
    },
    {
      title: i18n.t("groupManagement.editGroup"),
      description: i18n.t("groupManagement.editGroupDescription"),
      icon: <EditIcon style={{ fontSize: 48 }} />,
      path: "/group-management/groups/edit",
    },
  ];

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          {i18n.t("groupManagement.title")}
        </Title>
      </MainHeader>

      <Box className={classes.root}>
        <FormControl className={classes.select} variant="outlined" size="small">
          <InputLabel>{i18n.t("groupManagement.selectConnection")}</InputLabel>
          <Select
            value={selectedConnectionId}
            onChange={(e) => setSelectedConnectionId(e.target.value)}
            label={i18n.t("groupManagement.selectConnection")}
            disabled={loading}
          >
            {connections.map((conn) => (
              <MenuItem key={conn.id} value={String(conn.id)}>
                {conn.name} ({conn.channel}) — {conn.number || "-"}
              </MenuItem>
            ))}
            {!loading && connections.length === 0 && (
              <MenuItem disabled>
                {i18n.t("groupManagement.noConnections")}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : connections.length === 0 ? (
          <Box className={classes.emptyState}>
            <PeopleIcon style={{ fontSize: 64, marginBottom: 16 }} />
            <Typography variant="h6">
              {i18n.t("groupManagement.noConnectionsTitle")}
            </Typography>
            <Typography variant="body2">
              {i18n.t("groupManagement.noConnectionsHint")}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {cards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.path}>
                <Card
                  className={classes.card}
                  onClick={() => handleNavigate(card.path)}
                >
                  <CardContent>
                    <Box color="primary.main" mb={1}>
                      {card.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </MainContainer>
  );
};

export default GroupManagement;
